import { createContext, useState, ReactNode, Dispatch, SetStateAction, useContext } from "react"

interface ModalProviderProps {
    children: ReactNode;
}

interface ModalState {
    isModalShow: boolean;
    modalStatus: boolean;
    isModaltwoBtnShow: boolean;
    modalText: string;
    handleOkBtn: () => void;
    handleCancelBtn: () => void;
}

interface ModalContextType {
    modal: ModalState;
    setModal: Dispatch<SetStateAction<ModalState>>;
    resetModal: () => void;
    alertMessage: (text: string) => void;
    openAlertModal: (
        text: string,
        handleOkBtn: () => void,
        status: boolean
    ) => void;
    openTwoBtnAlertModal: (
        text: string, handleOkBtn: () => void, handleCancelBtn: () => void
    ) => void;
}

const initialState: ModalState = {
    isModalShow: false,
    modalStatus: false,
    isModaltwoBtnShow: false,
    modalText: "",
    handleOkBtn: () => {},
    handleCancelBtn: () => {}
}

const ModalContext = createContext<ModalContextType>({
    modal: initialState,
    setModal: () => {},
    resetModal: () => {},
    alertMessage: () => {},
    openAlertModal: () => {},
    openTwoBtnAlertModal: () => {}
});

export const ModalProvider = ({ children }: ModalProviderProps) => {

    const [modal, setModal] = useState<ModalState>(initialState);

    const resetModal = () => {
        setModal(initialState);
    }

    const openAlertModal = (text: string, handleOkBtn: () => void, status: boolean = false) => {
        setModal(state => ({
            ...state,
            isModalShow: true,
            isModaltwoBtnShow: false,
            modalStatus: status,
            modalText: text,
            handleOkBtn
        }));
    }

    const openTwoBtnAlertModal = (
        text: string, handleOkBtn: () => void, handleCancelBtn: () => void
    ) => {
        setModal(state => ({
            ...state,
            isModalShow: true,
            isModaltwoBtnShow: true,
            modalText: text,
            handleOkBtn,
            handleCancelBtn
        }));
    }

    const alertMessage = (text: string) => {
        openAlertModal(text, resetModal, false);
    }

    return (
        <ModalContext.Provider value={{
            modal, setModal, resetModal, alertMessage, openAlertModal, openTwoBtnAlertModal
        }}>
            {children}

            {modal.isModalShow && (
                <>
                    <div style={{
                        position: 'fixed', inset: 0,
                        background: 'rgba(0,0,0,0.45)',
                        zIndex: 7998,
                    }} />
                    <div style={{
                        position: 'fixed', inset: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        zIndex: 7999,
                    }}>
                        <div className="custom-modal-content" style={{ width: 400, maxWidth: 'calc(100vw - 32px)' }}>
                            <div style={{ padding: '32px 24px 20px', textAlign: 'center' }}>
                                <div style={{ marginBottom: 16, lineHeight: 1 }}>
                                    {modal.isModaltwoBtnShow ? (
                                        <i className="bi bi-question-circle-fill" style={{ color: 'var(--sook-primary)', fontSize: '4rem' }} />
                                    ) : modal.modalStatus ? (
                                        <i className="bi bi-check-circle-fill" style={{ color: '#22c55e', fontSize: '4rem' }} />
                                    ) : (
                                        <i className="bi bi-x-circle-fill" style={{ color: '#ef4444', fontSize: '4rem' }} />
                                    )}
                                </div>
                                <p style={{
                                    fontSize: '1rem',
                                    color: 'var(--sook-on-surface)',
                                    margin: 0,
                                    lineHeight: 1.6,
                                    wordBreak: 'break-word',
                                }}>
                                    {modal.modalText}
                                </p>
                            </div>

                            <div style={{ display: 'flex', gap: 12, padding: '0 24px 24px' }}>
                                {modal.isModaltwoBtnShow && (
                                    <button
                                        style={{
                                            flex: 1, height: 44,
                                            border: '1px solid var(--sook-outline-variant)',
                                            borderRadius: 8,
                                            background: 'var(--sook-surface-container-lowest)',
                                            color: 'var(--sook-on-surface)',
                                            fontWeight: 500, cursor: 'pointer',
                                            fontSize: '0.95rem',
                                        }}
                                        onClick={() => { modal.handleCancelBtn(); resetModal(); }}
                                    >
                                        ยกเลิก
                                    </button>
                                )}
                                <button
                                    style={{
                                        flex: 1, height: 44,
                                        border: 'none', borderRadius: 8,
                                        background: 'var(--sook-primary)',
                                        color: '#fff',
                                        fontWeight: 600, cursor: 'pointer',
                                        fontSize: '0.95rem',
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--sook-primary-strong)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'var(--sook-primary)'; }}
                                    onClick={() => { modal.handleOkBtn(); resetModal(); }}
                                >
                                    ยืนยัน
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </ModalContext.Provider>
    )

}

export const useAlertModal = () => {
    return useContext(ModalContext);
}
